import { useParams, Link } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import SEO from "@/components/SEO";
import { getProductBySlug } from "@/data/products";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { useState } from "react";
import { ArrowLeft, Check, Download, FileText, Info } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

import productModern from "@/assets/product-modern.png";
import productAdm from "@/assets/product-adm.png";
import productCondensed from "@/assets/product-condensed.png";
import { fontModern, fontAdm } from "@/data/products";

import trackDiagram from "@/assets/track-diagram.png";
import productTrackRivets from "@/assets/product-track-rivets.jpg";
import sellSheetFull from "@/assets/sell-sheet-full.jpg";
import cutSheetFull from "@/assets/cut-sheet-full.jpg";
import sellSheetCloseup from "@/assets/sell-sheet-closeup.jpg";
import sellSheetInstall from "@/assets/sell-sheet-install.jpg";
import cutSheetDiagram1 from "@/assets/cut-sheet-diagram-1.png";

import styleModern from "@/assets/style-modern.jpg";
import styleAdm from "@/assets/style-adm.jpg";
import styleCondensed from "@/assets/style-condensed.jpg";

const STYLE_IMAGES: Record<string, string> = {
  Modern: styleModern,
  ADM: styleAdm,
  Condensed: styleCondensed,
};

// Color availability from sell sheet
const COLOR_AVAILABILITY: Record<string, Record<string, { Black: boolean; Red: boolean; White: boolean }>> = {
  Modern: {
    '3″ on 3-7/8″': { Black: true, Red: true, White: true },
    '4″ on 4-7/8″': { Black: true, Red: true, White: true },
    '6″ on 6-7/8″': { Black: true, Red: true, White: true },
    '8″ on 8-7/8″': { Black: true, Red: true, White: false },
    '8″ on 9-7/8″': { Black: true, Red: true, White: true },
    '10″ on 11-7/8″': { Black: true, Red: true, White: false },
  },
  ADM: {
    '4″ on 4-7/8″': { Black: true, Red: true, White: true },
    '6″ on 6-7/8″': { Black: true, Red: true, White: true },
    '8″ on 8-7/8″': { Black: true, Red: true, White: false },
    '8″ on 9-7/8″': { Black: true, Red: true, White: true },
    '10″ on 11-7/8″': { Black: true, Red: true, White: false },
  },
  Condensed: {
    '4″ on 4-7/8″': { Black: true, Red: true, White: true },
    '6″ on 6-7/8″': { Black: true, Red: true, White: true },
    '8″ on 8-7/8″': { Black: true, Red: true, White: false },
    '8″ on 9-7/8″': { Black: true, Red: true, White: true },
    '10″ on 11-7/8″': { Black: true, Red: true, White: false },
  },
};

const STANDARD_SET_CONTENTS = [
  { chars: "E", qty: 7 },
  { chars: "A I", qty: 6 },
  { chars: "O U", qty: 5 },
  { chars: "L N R S T P", qty: 4 },
  { chars: "F G H M", qty: 3 },
  { chars: "B C D", qty: 3 },
  { chars: "J K V W Y Z", qty: 2 },
  { chars: "Q X $ ¢", qty: 1 },
  { chars: "0 1 2 3 4 5 6 7 8 9", qty: 1 },
];

export default function ProductDetail() {
  const { slug } = useParams<{ slug: string }>();
  const product = getProductBySlug(slug || "");
  const { addItem } = useCart();
  const { toast } = useToast();

  const [selectedSize, setSelectedSize] = useState<string>("");
  const [selectedColor, setSelectedColor] = useState<string>("");
  const [selectedVariant, setSelectedVariant] = useState<string>("");

  if (!product) {
    return (
      <Layout>
        <div className="container py-20 text-center">
          <h1 className="font-display text-3xl font-bold">Product not found</h1>
          <Button asChild variant="outline" className="mt-6">
            <Link to="/shop">Back to Shop</Link>
          </Button>
        </div>
      </Layout>
    );
  }

  const currentVariant = product.variants?.find((v) => v.label === selectedVariant);
  const currentSize = product.sizes?.find((s) => s.label === selectedSize);
  const displayPrice = currentVariant
    ? currentVariant.price
    : currentSize
    ? currentSize.price
    : product.price;

  // Filter available colors based on size + style selection
  const availableColors = product.style && selectedSize && COLOR_AVAILABILITY[product.style]?.[selectedSize]
    ? (["Black", "Red", "White"] as const).filter(c => COLOR_AVAILABILITY[product.style!]![selectedSize]?.[c])
    : product.colors || [];

  const canAdd =
    product.category === "letter-set"
      ? selectedSize && selectedColor
      : product.variants
        ? !!selectedVariant
        : true;

  const handleAdd = () => {
    if (!canAdd) return;
    if (selectedColor === "White") {
      toast({
        title: "White is discontinued",
        description: "Sorry — Gemini has discontinued White Pronto letters. Please choose Black or Red.",
        variant: "destructive",
      });
      return;
    }
    const variantLabel = selectedSize || selectedVariant || "";
    const variantId = `${product.slug}--${variantLabel
      .replace(/[^a-z0-9]+/gi, "-")
      .replace(/^-|-$/g, "")
      .toLowerCase()}`;
    addItem({
      productId: product.id,
      variantId,
      name: product.name,
      size: selectedSize || undefined,
      color: selectedColor || undefined,
      variant: selectedVariant || undefined,
      price: displayPrice,
      image: product.image,
    });
    toast({ title: "Added to cart", description: `${product.name} has been added to your cart.` });
  };

  // Reset color when size changes and color is no longer available
  const handleSizeChange = (size: string) => {
    setSelectedSize(size);
    if (product.style && COLOR_AVAILABILITY[product.style]?.[size]) {
      const avail = COLOR_AVAILABILITY[product.style][size];
      if (selectedColor && !avail[selectedColor as keyof typeof avail]) {
        setSelectedColor("");
      }
    }
  };

  const isLetterSet = product.category === "letter-set";

  // Build ProductGroup JSON-LD with per-variant Product entries.
  // This mirrors the 37 SKUs in feed.xml (7 groups). Variants share the
  // canonical product URL — no separate indexable variant URLs.
  const canonicalUrl = `https://pronto-letters.com/shop/${product.slug}`;
  const seller = {
    "@type": "Organization",
    name: "Moon River Signs dba Pronto-Letters.com",
    url: "https://pronto-letters.com",
  };
  const brand = { "@type": "Brand", name: "Gemini" };

  const slugify = (s: string) =>
    s.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase();

  const pricedVariants: Array<{ label: string; price: number; size?: string }> =
    product.sizes
      ? product.sizes.map((s) => ({ label: s.label, price: s.price, size: s.label }))
      : product.variants
        ? product.variants.map((v) => ({ label: v.label, price: v.price }))
        : [];

  const hasVariant = pricedVariants.map((v) => {
    const sku = `${product.slug}--${slugify(v.label)}`;
    const variantProduct: Record<string, unknown> = {
      "@type": "Product",
      name: `${product.name} — ${v.label}`,
      sku,
      mpn: sku,
      brand,
      image: `https://pronto-letters.com${product.image}`,
      url: canonicalUrl,
      isVariantOf: { "@type": "ProductGroup", productGroupID: product.slug },
      offers: {
        "@type": "Offer",
        url: canonicalUrl,
        priceCurrency: "USD",
        price: (v.price / 100).toFixed(2),
        availability: "https://schema.org/InStock",
        itemCondition: "https://schema.org/NewCondition",
        seller,
      },
    };
    if (v.size) variantProduct.size = v.size;
    return variantProduct;
  });

  const productSchema = {
    "@context": "https://schema.org",
    "@type": "ProductGroup",
    name: product.name,
    productGroupID: product.slug,
    description: product.description,
    brand,
    image: `https://pronto-letters.com${product.image}`,
    category: product.category === "letter-set" ? "Sign Letters" : "Sign Accessories",
    url: canonicalUrl,
    variesBy: product.category === "letter-set" ? ["size", "color"] : ["variant"],
    hasVariant,
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://pronto-letters.com/" },
      { "@type": "ListItem", position: 2, name: "Shop", item: "https://pronto-letters.com/shop" },
      {
        "@type": "ListItem",
        position: 3,
        name: product.name,
        item: `https://pronto-letters.com/shop/${product.slug}`,
      },
    ],
  };

  // Note: no FAQPage schema is emitted. Google's FAQ policy — and this
  // project's rule — requires the same Q&A to be visibly rendered on
  // the page. The bulleted feature list is not a Q&A UI, so emitting
  // FAQPage here would be schema spam.

  return (
    <Layout>
      <SEO
        title={product.name}
        description={`${product.description} Shop Gemini Pronto™ ${product.name} online with fast UPS Ground shipping.`}
        canonical={`/shop/${product.slug}`}
        ogType="product"
        jsonLd={[productSchema, breadcrumbSchema]}
      />
      <div className="container py-12">
        <Link to="/shop" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="mr-1 h-4 w-4" /> Back to Shop
        </Link>

        <div className="mt-8 grid gap-12 lg:grid-cols-2">
          {/* Image gallery */}
          <div className="space-y-4">
            {/* Main image — swaps when a variant with a matching image is selected */}
            {(() => {
              let displayImage = product.style && STYLE_IMAGES[product.style] ? STYLE_IMAGES[product.style] : product.image;
              if (product.variantImages && selectedVariant) {
                const matchKey = Object.keys(product.variantImages).find(k => selectedVariant.includes(k));
                if (matchKey) displayImage = product.variantImages[matchKey];
              }
              return (
                <div className="flex items-center justify-center overflow-hidden rounded-lg border bg-white p-6">
                  <img src={displayImage} alt={product.name} className="w-full object-contain transition-all duration-300" />
                </div>
              );
            })()}

            {/* Variant image thumbnails */}
            {product.variantImages && Object.keys(product.variantImages).length > 0 && (
              <div className="flex gap-3">
                {Object.entries(product.variantImages).map(([key, img]) => (
                  <button
                    key={key}
                    onClick={() => {
                      const match = product.variants?.find(v => v.label.includes(key));
                      if (match) setSelectedVariant(match.label);
                    }}
                    className={`flex-1 overflow-hidden rounded-lg border-2 bg-white p-2 transition-all ${
                      (selectedVariant ? selectedVariant.includes(key) : key === Object.keys(product.variantImages!)[0])
                        ? "border-accent"
                        : "border-border hover:border-muted-foreground"
                    }`}
                  >
                    <img src={img} alt={key} className="h-24 w-full object-contain" />
                    <p className="mt-1 text-center text-xs font-medium">{key}</p>
                  </button>
                ))}
              </div>
            )}




            {/* Standard Set Contents — moved to left column */}
            {isLetterSet && (
              <div className="mt-6 max-w-[70%]">
                <h3 className="font-display text-sm font-semibold">What's in a Set of 100?</h3>
                <Table className="mt-2 text-xs">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Characters</TableHead>
                      <TableHead className="text-right text-xs">Qty Each</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {STANDARD_SET_CONTENTS.map((row) => (
                      <TableRow key={row.chars}>
                        <TableCell className="font-mono text-xs py-1">{row.chars}</TableCell>
                        <TableCell className="text-right text-xs py-1">{row.qty}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Track — Visible Opening quick-ref + diagram in left column */}
            {product.slug === "pronto-track" && (
              <div className="mt-6 space-y-6 border-t pt-6">
                <div className="rounded-lg border p-4">
                  <p className="font-display text-sm font-semibold mb-2">Visible Opening per Row</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    {[["3″", '3¼"'], ["4″", '4¼"'], ["6″", '6¼"'], ["8 on 9″", '8¼"'], ["8″", '9¼"'], ["10″", '11¼"']].map(([letter, opening]) => (
                      <span key={letter}><strong className="text-foreground">{letter}</strong> letter → {opening}</span>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="font-display text-sm font-semibold">Track Profile Diagram</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">Top, Dual, and Bottom cross-sections with dimensions. <span className="text-accent">Click to zoom.</span></p>
                  <Dialog>
                    <DialogTrigger asChild>
                      <button className="mt-2 flex w-full cursor-zoom-in justify-center rounded-lg border bg-white p-4 transition-shadow hover:shadow-md">
                        <img src={trackDiagram} alt="Pronto Track profile diagram" className="max-h-72 object-contain" />
                      </button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl flex items-center justify-center bg-white p-8">
                      <img src={trackDiagram} alt="Pronto Track profile diagram — full size" className="w-full object-contain" />
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            )}

            {/* Compact sell sheet + cut sheet previews */}
            {isLetterSet && (
              <div className="mt-4 border-t pt-4 max-w-[70%]">
                <div className="grid grid-cols-2 gap-3">
                <Dialog>
                  <DialogTrigger asChild>
                    <button className="group overflow-hidden rounded-lg border bg-card transition-shadow hover:shadow-lg">
                      <img src={sellSheetFull} alt="Full Pronto sell sheet" className="w-full object-contain opacity-90 transition-opacity group-hover:opacity-100" />
                      <div className="border-t bg-secondary px-2 py-1 text-center text-xs font-medium">
                        View Sell Sheet
                      </div>
                    </button>
                  </DialogTrigger>
                  <DialogContent className="max-w-4xl">
                    <img src={sellSheetFull} alt="Full Pronto sell sheet" className="w-full" />
                  </DialogContent>
                </Dialog>
                <Dialog>
                  <DialogTrigger asChild>
                    <button className="group overflow-hidden rounded-lg border bg-card transition-shadow hover:shadow-lg">
                      <img src={cutSheetFull} alt="Technical cut sheet" className="w-full object-contain opacity-90 transition-opacity group-hover:opacity-100" />
                      <div className="border-t bg-secondary px-2 py-1 text-center text-xs font-medium">
                        View Cut Sheet
                      </div>
                    </button>
                  </DialogTrigger>
                  <DialogContent className="max-w-4xl">
                    <img src={cutSheetFull} alt="Full technical cut sheet" className="w-full" />
                  </DialogContent>
                </Dialog>
                </div>
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            {product.style && (
              <span className="text-sm font-medium uppercase tracking-wider text-accent">{product.style}</span>
            )}
            <h1 className="mt-1 font-display text-3xl font-bold">{product.name}</h1>
            <p className="mt-4 text-muted-foreground">{product.description}</p>

            {/* Key manufacturing info callout */}
            {isLetterSet && (
              <div className="mt-4 rounded-lg border border-accent/20 bg-accent/5 p-4">
                <div className="flex items-start gap-3">
                  <Info className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
                  <div className="text-sm">
                    <p className="font-semibold">Injection-Molded Quality</p>
                    <p className="mt-1 text-muted-foreground">
                      Each panel features raised edges to prevent scratching and molded-in feet that keep debris and ice from interfering with letter changes. UV-cured, water-based inks with zero VOCs.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {displayPrice > 0 && (
              <p className="mt-4 text-2xl font-bold">${(displayPrice / 100).toFixed(2)}</p>
            )}
            {isLetterSet && !selectedSize && (
              <p className="mt-4 text-sm text-muted-foreground">Select a size to see pricing</p>
            )}

            {/* Size selector */}
            {product.sizes && (
              <div className="mt-6">
                <label className="text-sm font-semibold">Size</label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {product.sizes.map((s) => (
                    <Button
                      key={s.label}
                      variant={selectedSize === s.label ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleSizeChange(s.label)}
                    >
                      {s.label} — ${(s.price / 100).toFixed(2)}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Color selector with availability */}
            {product.colors && (
              <div className="mt-6">
                <label className="text-sm font-semibold">
                  Color
                  {selectedSize && availableColors.length < 3 && (
                    <span className="ml-2 text-xs font-normal text-muted-foreground">(available for selected size)</span>
                  )}
                </label>
                <div className="mt-2 flex gap-2">
                  {(availableColors as string[]).map((c) => {
                    const isDiscontinued = c === "White";
                    return (
                      <Button
                        key={c}
                        variant={selectedColor === c ? "default" : "outline"}
                        size="sm"
                        aria-disabled={isDiscontinued}
                        className={isDiscontinued ? "line-through opacity-60 cursor-not-allowed" : ""}
                        onClick={() => {
                          if (isDiscontinued) {
                            toast({
                              title: "White is discontinued",
                              description: "Sorry — Gemini has discontinued White Pronto letters. Please choose Black or Red.",
                              variant: "destructive",
                            });
                            return;
                          }
                          setSelectedColor(c);
                        }}
                      >
                        {isDiscontinued ? "White — Discontinued" : c}
                      </Button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Variant selector (accessories) */}
            {product.variants && (
              <div className="mt-6">
                <label className="text-sm font-semibold">Option</label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {product.variants.map((v) => (
                    <Button
                      key={v.label}
                      variant={selectedVariant === v.label ? "default" : "outline"}
                      size="sm"
                      onClick={() => setSelectedVariant(v.label)}
                    >
                      {v.label} — ${(v.price / 100).toFixed(2)}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            <Button
              className="mt-8 bg-accent text-accent-foreground hover:bg-accent/90"
              size="lg"
              disabled={!canAdd}
              onClick={handleAdd}
            >
              <Check className="mr-2 h-4 w-4" /> Add to Cart
            </Button>

            {/* Features */}
            {product.features && (
              <div className="mt-10">
                <h3 className="font-display text-lg font-semibold">Key Features</h3>
                <ul className="mt-3 space-y-2">
                  {product.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            )}



            {/* Pronto Track — Installation & Dimensions */}
            {product.slug === "pronto-track" && (
              <div className="mt-10 space-y-8">

                {/* PDF download */}
                <a
                  href="/docs/Pronto-Track-Guide.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-lg border bg-card px-4 py-2 text-sm font-medium transition-colors hover:bg-secondary"
                >
                  <Download className="h-4 w-4 text-accent" />
                  Pronto Track Installation Guide (PDF)
                </a>

                {/* Installation methods */}
                <div>
                  <h3 className="font-display text-lg font-semibold">Installation</h3>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Accurate spacing is the key to a great Pronto changeable letter system. Use a chalk line and level to mark the location for your first piece of track.
                  </p>
                  <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    <div className="rounded-lg border p-4">
                      <p className="text-sm font-semibold">Rivet Installation</p>
                      <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                        <li>• Drill hole through track and background (max 8″ center to center)</li>
                        <li>• For 1/8″–3/16″ background, drill 3/16″ diameter hole</li>
                        <li>• Insert Pronto rivet, push pin to closed position</li>
                      </ul>
                    </div>
                    <div className="rounded-lg border p-4">
                      <p className="text-sm font-semibold">Adhesive Installation</p>
                      <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                        <li>• Apply WELD-ON #3 or #4 to back of track</li>
                        <li>• Use syringe or thin-needle bottle applicator</li>
                        <li>• Tape in place until adhesive sets completely</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Sign height reference table */}
                <div>
                  <h3 className="font-display text-lg font-semibold">Total Sign Height by Lines of Copy</h3>
                  <p className="mt-1 text-xs text-muted-foreground">Visible opening dimensions for standard track spacing.</p>
                  <div className="mt-3 overflow-x-auto">
                    <Table className="text-xs">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs">Lines</TableHead>
                          <TableHead className="text-center text-xs">3″</TableHead>
                          <TableHead className="text-center text-xs">4″</TableHead>
                          <TableHead className="text-center text-xs">6″</TableHead>
                          <TableHead className="text-center text-xs">8 on 9″</TableHead>
                          <TableHead className="text-center text-xs">8″</TableHead>
                          <TableHead className="text-center text-xs">10″</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {[
                          ["1", '5½"', '6½"', '6"', '8"', '9"', '10"'],
                          ["2", '10⅜"', '12⅜"', '16⅜"', '20⅜"', '22⅜"', '26⅜"'],
                          ["3", '15¼"', '18¼"', '24¼"', '30¼"', '33¼"', '39¼"'],
                          ["4", '20⅛"', '24⅛"', '32⅛"', '40⅛"', '44⅛"', '52⅛"'],
                          ["5", '25"', '30"', '40"', '50"', '55"', '65"'],
                        ].map(([lines, ...vals]) => (
                          <TableRow key={lines}>
                            <TableCell className="font-medium text-xs">{lines}</TableCell>
                            {vals.map((v, i) => <TableCell key={i} className="text-center text-xs">{v}</TableCell>)}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </div>
            )}

            {/* Track Rivets — image zoom + PDF */}
            {product.slug === "track-rivets" && (
              <div className="mt-10 space-y-6">
                <Dialog>
                  <DialogTrigger asChild>
                    <button className="group flex w-full cursor-zoom-in items-center justify-center overflow-hidden rounded-lg border bg-white p-6 transition-shadow hover:shadow-md">
                      <img src={productTrackRivets} alt="Pronto Track Rivet" className="max-h-64 object-contain transition-transform duration-300 group-hover:scale-105" />
                      <span className="sr-only">Click to zoom</span>
                    </button>
                  </DialogTrigger>
                  <DialogContent className="max-w-xl flex items-center justify-center bg-white p-10">
                    <img src={productTrackRivets} alt="Pronto Track Rivet — full size" className="w-full object-contain" />
                  </DialogContent>
                </Dialog>
                <a
                  href="/docs/Track-Rivets-Guide.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-lg border bg-card px-4 py-2 text-sm font-medium transition-colors hover:bg-secondary"
                >
                  <Download className="h-4 w-4 text-accent" />
                  Track Rivets Info Sheet (PDF)
                </a>
              </div>
            )}

            {/* Size / color availability table for letter sets with known data */}
            {isLetterSet && product.style && COLOR_AVAILABILITY[product.style] && (
              <div className="mt-10">
                <h3 className="font-display text-lg font-semibold">Color Availability by Size</h3>
                <Table className="mt-3">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Size</TableHead>
                      <TableHead className="text-center">Black</TableHead>
                      <TableHead className="text-center">Red</TableHead>
                      <TableHead className="text-center">White</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Object.entries(COLOR_AVAILABILITY[product.style!]).map(([size, colors]) => (
                      <TableRow key={size}>
                        <TableCell className="text-sm">{size}</TableCell>
                        <TableCell className="text-center">{colors.Black ? "●" : "—"}</TableCell>
                        <TableCell className="text-center">{colors.Red ? "●" : "—"}</TableCell>
                        <TableCell className="text-center">{colors.White ? "●" : "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

          </div>
        </div>
      </div>
    </Layout>
  );
}
